import { useEffect, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore, API_BASE, apiFetch } from '../store/auth'
import {
  Button,
  PageLoading,
  PageHeader,
  FilterBar,
  SearchFilter,
  SelectFilter,
  Table,
  Badge,
  Card,
  NoOrganizationMessage,
  Input,
  FormField,
} from '../components'
import type { Invoice, CreateInvoiceRequest } from '../api/types'

// Format currency
function formatEuro(cents: number): string {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100)
}

// Format date
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fi-FI')
}

// Get status badge
function getStatusBadge(status: Invoice['status']) {
  switch (status) {
    case 'draft':
      return <Badge variant="neutral">Luonnos</Badge>
    case 'sent':
      return <Badge variant="warning">Lähetetty</Badge>
    case 'paid':
      return <Badge variant="success">Maksettu</Badge>
    case 'overdue':
      return <Badge variant="error">Erääntynyt</Badge>
    case 'cancelled':
      return <Badge variant="neutral">Peruttu</Badge>
    default:
      return <Badge variant="neutral">{status}</Badge>
  }
}

export default function InvoicePage() {
  const { t } = useTranslation()
  const { activeOrg } = useAuthStore()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    if (!activeOrg) return
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ organization_id: activeOrg.id })
      if (statusFilter) params.append('status', statusFilter)
      
      const response = await apiFetch(`${API_BASE}/invoices?${params}`)
      if (!response.ok) throw new Error('Laskujen lataus epäonnistui')
      
      const data = await response.json()
      setInvoices(data.invoices || [])
    } catch {
      setError('Laskujen lataus epäonnistui')
    } finally {
      setLoading(false)
    }
  }, [activeOrg, statusFilter])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) =>
      filter === '' ||
      inv.customerEmail.toLowerCase().includes(filter.toLowerCase()) ||
      inv.customerName?.toLowerCase().includes(filter.toLowerCase()) ||
      inv.invoiceNumber.toLowerCase().includes(filter.toLowerCase())
    )
  }, [invoices, filter])

  // Send invoice
  const handleSend = async (invoiceId: string) => {
    if (!confirm('Lähetetäänkö lasku asiakkaalle?')) return
    try {
      const response = await apiFetch(`${API_BASE}/invoices/${invoiceId}/send`, {
        method: 'POST',
      })
      if (!response.ok) throw new Error('Lähetys epäonnistui')
      await fetchInvoices()
    } catch {
      setError('Laskun lähetys epäonnistui')
    }
  }

  // Send reminder
  const handleRemind = async (invoiceId: string) => {
    if (!confirm('Lähetetäänkö maksumuistutus?')) return
    try {
      const response = await apiFetch(`${API_BASE}/invoices/${invoiceId}/remind`, {
        method: 'POST',
      })
      if (!response.ok) throw new Error(t('invoices.reminderFailed'))
      await fetchInvoices()
    } catch {
      setError(t('invoices.reminderFailed'))
    }
  }

  // Cancel invoice
  const handleCancel = async (invoiceId: string) => {
    if (!confirm(t('invoices.cancelConfirm'))) return
    try {
      const response = await apiFetch(`${API_BASE}/invoices/${invoiceId}/cancel`, {
        method: 'POST',
      })
      if (!response.ok) throw new Error(t('invoices.cancelFailed'))
      await fetchInvoices()
    } catch {
      setError('Laskun peruminen epäonnistui')
    }
  }

  // Create invoice
  const handleCreate = async (data: CreateInvoiceRequest) => {
    try {
      const response = await apiFetch(`${API_BASE}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!response.ok) throw new Error('Laskun luonti epäonnistui')
      setShowCreateModal(false)
      await fetchInvoices()
    } catch {
      setError('Laskun luonti epäonnistui')
    }
  }

  if (!activeOrg) return <NoOrganizationMessage />
  if (loading) return <PageLoading message="Ladataan laskuja..." />

  const statusOptions = [
    { value: '', label: 'Kaikki tilat' },
    { value: 'draft', label: t('common.drafts') },
    { value: 'sent', label: 'Lähetetyt' },
    { value: 'paid', label: 'Maksetut' },
    { value: 'overdue', label: 'Erääntyneet' },
    { value: 'cancelled', label: 'Perutut' },
  ]

  const columns = [
    {
      key: 'invoice',
      header: 'Lasku',
      render: (inv: Invoice) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{inv.invoiceNumber}</div>
          <div className="text-xs text-gray-500">{formatDate(inv.issueDate)}</div>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Asiakas',
      render: (inv: Invoice) => (
        <div>
          <div className="text-sm text-gray-900">{inv.customerName || inv.customerEmail}</div>
          <div className="text-xs text-gray-500">{inv.customerEmail}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Summa',
      align: 'right' as const,
      render: (inv: Invoice) => (
        <span className="font-medium">{formatEuro(inv.totalAmount)}</span>
      ),
    },
    {
      key: 'due',
      header: 'Eräpäivä',
      render: (inv: Invoice) => (
        <div>
          <div className="text-sm">{formatDate(inv.dueDate)}</div>
          {inv.status === 'overdue' && (
            <div className="text-xs text-red-600">Erääntynyt</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Tila',
      align: 'center' as const,
      render: (inv: Invoice) => getStatusBadge(inv.status),
    },
    {
      key: 'actions',
      header: 'Toiminnot',
      align: 'right' as const,
      render: (inv: Invoice) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setSelectedInvoice(inv)}
          >
            👁️ Näytä
          </Button>
          {inv.status === 'draft' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleSend(inv.id)}
            >
              📤 Lähetä
            </Button>
          )}
          {inv.status === 'sent' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleRemind(inv.id)}
            >
              🔔 Muistuta
            </Button>
          )}
          {(inv.status === 'draft' || inv.status === 'sent') && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleCancel(inv.id)}
            >
              🚫 Peru
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={`📄 Laskutus — ${activeOrg.name}`}
        action={
          <Button onClick={() => setShowCreateModal(true)}>
            + Uusi lasku
          </Button>
        }
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <FilterBar>
        <SearchFilter
          value={filter}
          onChange={setFilter}
          placeholder={t('invoices.searchInvoices')}
        />
        <SelectFilter
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusOptions}
          placeholder="Tila"
        />
      </FilterBar>

      <Card>
        {filteredInvoices.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-4">📄</div>
            <p className="text-lg font-medium">Ei laskuja</p>
            <p className="text-sm">Luo ensimmäinen lasku yllä olevalla napilla</p>
          </div>
        ) : (
          <Table
            data={filteredInvoices}
            columns={columns}
            keyExtractor={(inv) => inv.id}
          />
        )}
      </Card>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          organizationId={activeOrg.id}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
        />
      )}

      {/* View Invoice Modal */}
      {selectedInvoice && (
        <ViewInvoiceModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  )
}

// Create Invoice Modal Component
interface CreateInvoiceModalProps {
  organizationId: string
  onClose: () => void
  onSubmit: (data: CreateInvoiceRequest) => void
}

function CreateInvoiceModal({ organizationId, onClose, onSubmit }: CreateInvoiceModalProps) {
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0, vat_rate: 0 }])

  const handleAddItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, vat_rate: 0 }])
  }

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const handleItemChange = (index: number, field: string, value: string | number) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], [field]: value }
    setItems(newItems)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      organization_id: organizationId,
      customer_email: customerEmail,
      customer_name: customerName,
      due_date: dueDate,
      items: items.map(item => ({
        description: item.description,
        quantity: Number(item.quantity),
        unit_price: Math.round(Number(item.unit_price) * 100), // Convert to cents
        vat_rate: Number(item.vat_rate),
      })),
    })
  }

  const totalAmount = items.reduce((sum, item) => 
    sum + (item.quantity * item.unit_price * (1 + item.vat_rate / 100)), 0
  )

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Uusi lasku</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Asiakkaan sähköposti">
              <Input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                required
              />
            </FormField>
            <FormField label="Asiakkaan nimi">
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </FormField>
          </div>
          <FormField label="Eräpäivä">
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
            />
          </FormField>
          
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-medium">Laskurivit</h3>
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  <Input
                    placeholder="Kuvaus"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    placeholder="Määrä"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    min="1"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    placeholder="Hinta (€)"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    placeholder="ALV %"
                    value={item.vat_rate}
                    onChange={(e) => handleItemChange(index, 'vat_rate', e.target.value)}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="col-span-1">
                  {items.length > 1 && (
                    <Button type="button" variant="danger" size="sm" onClick={() => handleRemoveItem(index)}>
                      🗑️
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <Button type="button" variant="secondary" onClick={handleAddItem}>
              + Lisää rivi
            </Button>
          </div>

          <div className="text-right text-lg font-bold">
            Yhteensä: {formatEuro(Math.round(totalAmount * 100))}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={onClose}>
              Peruuta
            </Button>
            <Button type="submit">
              Luo lasku
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// View Invoice Modal Component
interface ViewInvoiceModalProps {
  invoice: Invoice
  onClose: () => void
}

function ViewInvoiceModal({ invoice, onClose }: ViewInvoiceModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Lasku {invoice.invoiceNumber}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ×
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Asiakas</h3>
              <p className="text-gray-900">{invoice.customerName || invoice.customerEmail}</p>
              <p className="text-gray-500">{invoice.customerEmail}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Tila</h3>
              <p>{getStatusBadge(invoice.status)}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Laskutuspäivä</h3>
              <p>{formatDate(invoice.issueDate)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Eräpäivä</h3>
              <p>{formatDate(invoice.dueDate)}</p>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Summa</h3>
            <p className="text-2xl font-bold">{formatEuro(invoice.totalAmount)}</p>
          </div>
          {invoice.paidAt && (
            <div>
              <h3 className="text-sm font-medium text-gray-500">Maksettu</h3>
              <p>{formatDate(invoice.paidAt)}</p>
              <p>{formatEuro(invoice.paidAmount || 0)}</p>
            </div>
          )}
          {invoice.stripePaymentLink && invoice.status !== 'paid' && (
            <div className="pt-4">
              <a
                href={invoice.stripePaymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                🔗 Maksulinkki
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
